import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { OktaAuth, Tokens } from "@okta/okta-auth-js";
import OktaSignIn from "@okta/okta-signin-widget";
import "@okta/okta-signin-widget/css/okta-sign-in.min.css";
import _ from "lodash";
import { get, isEmpty, pick } from "radash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { LStorage } from "@utils/localStorage";

import AuthService from "../../services/authService";
import { parseQueryParams, useQuery } from "../../services/utils";
import { injectAuthUser } from "../../store/actions/auth";

// *** TEMPORARILY DISABLED ***

// Custom styles for Okta widget
const customStyles = `
  #okta-sign-in {
    margin-top: 20px !important;
    padding-bottom: 20px !important;
    border-radius: 8px !important;
  }
`;

// Okta configuration
const oktaConfig = {
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID!,
  issuer: process.env.REACT_APP_OKTA_ISSUER!,
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: ["openid", "profile", "email"],
  responseType: ["code"],
  pkce: true,
  useClassicEngine: true,
};

const ResmedSignIn: React.FC<{
  handleRegistrationLogin?: (data: any) => void;
  source: "registration" | "login";
  registrationFormData?: Record<string, any>;
}> = ({
  source = "login",
  handleRegistrationLogin,
  registrationFormData = {},
}) => {
  const widgetRef = useRef<any>(null);
  const oktaSignIn = useRef<any>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const [loading, setLoading] = useState(source !== "registration");
  const [autoLogin, setAutoLogin] = useState(false);

  const initializeWidget = async () => {
    try {
      const OktaAuthUtils = new OktaAuth({
        clientId: process.env.REACT_APP_OKTA_CLIENT_ID as string,
        issuer: process.env.REACT_APP_OKTA_ISSUER as string,
        scopes: ["openid", "profile", "email"],
        responseType: ["code"],
        pkce: true,
      });

      const revokeSession = async (reset: boolean) => {
        await OktaAuthUtils.revokeAccessToken();
        await OktaAuthUtils.clearStorage();
        await OktaAuthUtils.closeSession();

        if (!reset) return;
        if (oktaSignIn.current) {
          oktaSignIn.current.remove();
        }

        initializeWidget();
      };

      // Handle existing session
      if (source !== "registration") {
        const session: any = await OktaAuthUtils.session
          .refresh()
          .catch(() => null);

        const tokens: any = await OktaAuthUtils.token
          .getWithoutPrompt({
            redirectUri: `${window.location.origin}/login/callback`,
          })
          .then((data) => {
            // Do something with tokens, such as
            const tokens = get(data, "tokens", {});
            OktaAuthUtils.tokenManager.setTokens(tokens as Tokens);
            return tokens;
          })
          .catch((err) => console.log(err.message));

        if (session?.status === "ACTIVE" && tokens?.accessToken) {
          setAutoLogin(true);
          setLoading(false);

          const user = await OktaAuthUtils.token.getUserInfo();
          const data = { ...user, ...registrationFormData };
          return await handleSuccessfulLogin(tokens, data, revokeSession);
        }
      }

      // Initialize Okta SignIn widget with config
      oktaSignIn.current = new OktaSignIn({
        ...oktaConfig,
        el: widgetRef.current,
        authParams: {
          pkce: true,
          responseType: ["code"],
          display: "page",
          issuer: oktaConfig.issuer,
        },
        features: {
          registration: false,
          rememberMe: false,
          autoPush: true,
        },
        i18n: {
          en: {
            "errors.E0000004":
              "Login failed. Check credentials or contact support.",
            "primaryauth.title": "Sign in with Resmed",
          },
        },
        logo: "/assets/external-logos/resmed-logo-large.svg",
        brandName: "Resmed",
      });

      oktaSignIn.current.on("afterError", (context: any, error: any) => {
        // TODO: Cleaner way of tracking errors
        const input = document.getElementById(
          "okta-signin-username",
        ) as HTMLInputElement;

        const data: any = {
          changeDetails: {
            username: input?.value || "N/A",
            loginType: "Resmed",
            loginSource: source,
            operatorRole: "Patient",
            action: "Resmed Okta Error",
            error: {
              ...get(error, "xhr.responseJSON", {}),
              errorStatus: get(error, "xhr.status", null),
              errorMessage: get(error, "message", "N/A"),
            },
          },
        };

        AuthService.logFailedLogin(data).catch(() => null);
      });

      // Search for URL Parameters
      const searchParams = new URL(window.location.href).searchParams;
      oktaSignIn.current.otp = searchParams.get("otp");
      oktaSignIn.current.state = searchParams.get("state");

      setTimeout(() => {
        setLoading(false);
      }, 2000);

      // Render widget and handle authentication flow
      oktaSignIn.current
        .showSignInToGetTokens({
          el: widgetRef.current,
        })
        .then(async (tokens: any) => {
          oktaSignIn.current.authClient.tokenManager.setTokens(tokens);
          const user = await oktaSignIn.current.authClient.token.getUserInfo();
          const data = { ...user, ...registrationFormData };
          await handleSuccessfulLogin(tokens, data, revokeSession);
        })
        .catch((err: Error) => {
          toast.error("Failed to authenticate. Please try again.");

          setAutoLogin(false);
          setLoading(false);
          revokeSession(true);
        });
    } catch (err) {
      console.error(err);
      setAutoLogin(false);
      setLoading(false);
      toast.error("Failed to initialize authentication");
    }
  };

  useEffect(() => {
    // Save resmed metadata
    const metadata = parseQueryParams(query, ["token"]);
    if (!isEmpty(metadata)) {
      LStorage.set("referralMetadata", JSON.stringify(metadata));
    }

    // Add custom styles to head
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);

    initializeWidget();

    return () => {
      document.head.removeChild(styleSheet);
      if (oktaSignIn.current) {
        oktaSignIn.current.remove();
      }
    };
  }, []);

  const handleSuccessfulLogin = async (
    tokens: any,
    user: any,
    revokeSession?: (reset: boolean) => Promise<void>,
  ) => {
    try {
      // Exchange Okta tokens for Ognomy tokens
      const accessToken = _.get(tokens, "accessToken.accessToken", "");
      const metadata = parseQueryParams(query, ["token"]);
      const loginBody = {
        userDetails: {
          ...user,
          externalId: user.sub,
        },
        resmedAccessToken: accessToken,
        referralMetaData: metadata,
      };

      const response = await AuthService.resmedSocialLogin(loginBody)
        .then(async (res) => {
          // If no user exists yet, redirect to new user registration
          if (res.data.genericLogin) {
            toast.info(
              "You have an Ognomy account with this email. You will be redirected to login shortly...",
              {
                autoClose: 4000,
                bodyStyle: {
                  whiteSpace: "pre-line",
                },
              },
            );

            if (revokeSession) {
              await revokeSession(false).catch(() => null);
            }

            setTimeout(() => {
              history.push(`/signInPage?email=${user.email}`);
            }, 5000);

            return null;
          }

          return res;
        })
        .catch(async (error) => {
          toast.error(
            error.message ||
              "Something went wrong, we're unable to sign you in with Resmed!",
          );

          if (revokeSession) {
            revokeSession(true).catch(() => null);
          }
        });

      if (!response) return;

      // Save auth data and update user context
      AuthService.saveAuth(_.omit(response.data, "isNewUser"));
      dispatch(injectAuthUser(response.data.user));

      // Remove resmed metadata
      LStorage.remove("referralMetadata");

      // Handle registration flow
      if (
        response.data.isNewUser &&
        source === "registration" &&
        handleRegistrationLogin
      ) {
        const data = pick(response.data.user as any, [
          "email",
          "firstName",
          "lastName",
        ]);

        return handleRegistrationLogin(data);
      } else if (response.data.isNewUser) {
        history.push("/resmed", {
          noEmailAuthRegistration: true,
          ...response.data,
        });
      } else {
        // Handle redirects based on query params
        const redirectPath = query.get("redirect") || "/patientDashboardPage";
        history.replace(redirectPath);
      }
    } catch (err) {
      console.log(err);
      toast.error(
        "Something went wrong, we're unable to sign you in with Resmed!",
      );

      if (revokeSession) {
        revokeSession(true).catch(() => null);
      }
    }
  };

  if (source === "registration")
    return <div ref={widgetRef} style={{ width: "100%" }} />;

  const isLoading = loading || autoLogin;
  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        maxWidth: 600,
        mx: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {loading && <CircularProgress sx={{ color: "text.primary" }} />}

      {autoLogin && (
        <Paper
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            maxWidth: 400,
            width: "100%",
          }}
        >
          <img
            src="/assets/external-logos/resmed-logo-large.svg"
            alt="Resmed Logo"
            width={170}
          />
          <Typography variant="h6" my={1} mt={2} textAlign="center">
            Logging you in...
          </Typography>
          <CircularProgress size={20} sx={{ color: "text.primary" }} />
        </Paper>
      )}

      {source === "login" && !isLoading && (
        <Box
          sx={{
            ml: 2,
            display: "flex",
            alignItems: "center",
            alignSelf: "flex-start",
          }}
        >
          <IconButton onClick={() => history.push("/resmed")}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            Back to Ognomy
          </Typography>
        </Box>
      )}
      <div
        ref={widgetRef}
        style={{ width: "100%", display: isLoading ? "none" : "block" }}
      />
    </Box>
  );
};

export default ResmedSignIn;
